import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ShoppingBag, Package, RotateCcw, Wrench, FileText, MessageCircle,
  ChevronLeft, Phone, Mail, MapPin, CheckCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { PageContainer } from '../components/layout/PageContainer'
import { Button } from '../components/ui/button'

// ─── Types ────────────────────────────────────────────────────────────────────

type CategoryId = 'verkoop' | 'bestelling' | 'retour' | 'technisch' | 'factuur' | 'overig'
type Step = 'category' | 'form' | 'success'

interface FormState {
  name: string; email: string; phone: string; company: string
  productQuery: string; quantity: string
  orderNumber: string; orderSubject: string
  returnReason: string; serialNumber: string
  productMpn: string; machineInfo: string
  invoiceNumber: string
  message: string
}

type FieldErrors = Partial<Record<keyof FormState, string>>

const CATEGORY_ICONS: Record<CategoryId, React.FC<{ className?: string }>> = {
  verkoop: ShoppingBag, bestelling: Package, retour: RotateCcw,
  technisch: Wrench, factuur: FileText, overig: MessageCircle,
}

const CATEGORY_IDS: CategoryId[] = ['verkoop', 'bestelling', 'retour', 'technisch', 'factuur', 'overig']

const INITIAL_FORM: FormState = {
  name: '', email: '', phone: '', company: '',
  productQuery: '', quantity: '',
  orderNumber: '', orderSubject: '',
  returnReason: '', serialNumber: '',
  productMpn: '', machineInfo: '',
  invoiceNumber: '',
  message: '',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function validateForm(category: CategoryId, f: FormState): FieldErrors {
  const e: FieldErrors = {}
  if (!f.name.trim()) e.name = 'Naam is verplicht'
  if (!f.email.trim() || !/\S+@\S+\.\S+/.test(f.email)) e.email = 'Geldig e-mailadres is verplicht'

  const req = (field: keyof FormState, label: string) => {
    if (!(f[field] as string).trim()) e[field] = `${label} is verplicht`
  }

  if (category === 'verkoop')    { req('productQuery', 'Product of MPN'); req('company', 'Bedrijfsnaam'); req('message', 'Omschrijving') }
  if (category === 'bestelling') { req('orderNumber', 'Ordernummer'); req('orderSubject', 'Onderwerp'); req('message', 'Toelichting') }
  if (category === 'retour')     { req('orderNumber', 'Ordernummer'); req('returnReason', 'Reden'); req('message', 'Toelichting') }
  if (category === 'technisch')  { req('message', 'Beschrijving') }
  if (category === 'factuur')    { req('invoiceNumber', 'Factuurnummer'); req('message', 'Toelichting') }
  if (category === 'overig')     { req('message', 'Bericht') }

  return e
}

// ─── Field helpers ────────────────────────────────────────────────────────────

function Field({ label, required, error, children }: {
  label: string; required?: boolean; error?: string; children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-neutral-700">
        {label}{required && <span className="text-primary ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

const inputCls = (error?: string) =>
  `w-full border rounded-md px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary ${error ? 'border-red-400' : 'border-neutral-200'}`

// ─── Category-specific fields (NL labels — field-level doesn't vary by lang) ──

function CategoryFields({ category, form, errors, onChange }: {
  category: CategoryId; form: FormState; errors: FieldErrors
  onChange: (field: keyof FormState, value: string) => void
}) {
  const inp = (field: keyof FormState, label: string, placeholder: string, required = true, type = 'text') => (
    <Field label={label} required={required} error={errors[field]}>
      <input type={type} className={inputCls(errors[field])} placeholder={placeholder}
        value={form[field] as string} onChange={e => onChange(field, e.target.value)} />
    </Field>
  )
  const sel = (field: keyof FormState, label: string, options: { value: string; label: string }[]) => (
    <Field label={label} required error={errors[field]}>
      <select className={inputCls(errors[field])} value={form[field] as string}
        onChange={e => onChange(field, e.target.value)}>
        <option value="">Kies…</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  )
  const ta = (field: keyof FormState, label: string, placeholder: string, required = true, rows = 4) => (
    <Field label={label} required={required} error={errors[field]}>
      <textarea rows={rows} className={inputCls(errors[field]) + ' resize-none'}
        placeholder={placeholder} value={form[field] as string}
        onChange={e => onChange(field, e.target.value)} />
    </Field>
  )

  if (category === 'verkoop') return <>
    {inp('productQuery', 'Product of MPN', "Bijv. X20CP1586 of '2x AI module voor X20'")}
    {inp('quantity', 'Gewenst aantal', 'Bijv. 5', false)}
    {ta('message', 'Omschrijving', 'Beschrijf uw aanvraag en het doel...')}
  </>

  if (category === 'bestelling') return <>
    {inp('orderNumber', 'Ordernummer', 'Bijv. ORD-2024-00123')}
    {sel('orderSubject', 'Onderwerp', [
      { value: 'status',    label: 'Leveringsstatus opvragen' },
      { value: 'wijzigen',  label: 'Bestelling wijzigen' },
      { value: 'annuleren', label: 'Bestelling annuleren' },
      { value: 'anders',    label: 'Anders' },
    ])}
    {ta('message', 'Toelichting', 'Geef een toelichting op uw vraag...')}
  </>

  if (category === 'retour') return <>
    {inp('orderNumber', 'Ordernummer', 'Bijv. ORD-2024-00123')}
    {sel('returnReason', 'Reden retour', [
      { value: 'defect',     label: 'Product is defect / DOA' },
      { value: 'verkeerd',   label: 'Verkeerd product ontvangen' },
      { value: 'beschadigd', label: 'Beschadigd geleverd' },
      { value: 'garantie',   label: 'Garantieclaim' },
      { value: 'anders',     label: 'Anders' },
    ])}
    {inp('serialNumber', 'Serienummer', 'Bijv. SN-20240001', false)}
    {ta('message', 'Toelichting', 'Beschrijf het probleem zo concreet mogelijk...')}
  </>

  if (category === 'technisch') return <>
    {inp('productMpn', 'Product / MPN', 'Bijv. X20CP1586', false)}
    {inp('machineInfo', 'Machine / installatie', 'Bijv. B&R X20 PLC in verpakkingsmachine', false)}
    {ta('message', 'Beschrijving', 'Beschrijf uw vraag of probleem...', true, 5)}
  </>

  if (category === 'factuur') return <>
    {inp('invoiceNumber', 'Factuurnummer', 'Bijv. F-2024-00456')}
    {ta('message', 'Toelichting', 'Omschrijf uw vraag of de fout op de factuur...')}
  </>

  return ta('message', 'Bericht', 'Stel uw vraag...', true, 5)
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ContactPage() {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('category')
  const [category, setCategory] = useState<CategoryId | null>(null)
  const [form, setForm] = useState<FormState>(INITIAL_FORM)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)

  function handleChange(field: keyof FormState, value: string) {
    setForm(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => { const n = { ...prev }; delete n[field]; return n })
  }

  function selectCategory(id: CategoryId) {
    setCategory(id)
    setStep('form')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!category) return
    const errs = validateForm(category, form)
    if (Object.keys(errs).length > 0) { setErrors(errs); return }
    setSubmitting(true)
    try {
      await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, ...form }),
      })
      setStep('success')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {
      toast.error('Versturen mislukt. Stuur een e-mail naar info@globalcontrol.group.')
    } finally {
      setSubmitting(false)
    }
  }

  const categoryLabel = category ? t(`contact.categories.${category}.label`) : ''

  return (
    <div>
      <div className="bg-gcg-dark text-white py-14 border-b-[3px] border-primary">
        <PageContainer>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">{t('contact.heading')}</h1>
          <p className="text-white/70 text-base">{t('contact.subheading')}</p>
        </PageContainer>
      </div>

      <PageContainer className="py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* ── Left: flow ── */}
          <div className="lg:col-span-2">

            {/* Step 1 */}
            {step === 'category' && (
              <div>
                <p className="text-xs font-bold tracking-[0.14em] uppercase text-neutral-400 mb-5">
                  {t('contact.step1Label')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {CATEGORY_IDS.map(id => {
                    const Icon = CATEGORY_ICONS[id]
                    return (
                      <button
                        key={id}
                        onClick={() => selectCategory(id)}
                        className="flex items-start gap-4 border border-neutral-200 bg-white p-5 text-left hover:border-primary hover:-translate-y-px transition-all duration-150 group"
                      >
                        <div className="flex items-center justify-center h-10 w-10 rounded-md bg-gcg-tint shrink-0 group-hover:bg-primary/10 transition-colors">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-neutral-900 mb-0.5">
                            {t(`contact.categories.${id}.label`)}
                          </p>
                          <p className="text-xs text-neutral-500 leading-snug">
                            {t(`contact.categories.${id}.desc`)}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Step 2 */}
            {step === 'form' && category && (
              <form onSubmit={handleSubmit} noValidate>
                <div className="flex items-center gap-3 mb-6">
                  <button type="button"
                    onClick={() => { setStep('category'); setErrors({}) }}
                    className="flex items-center gap-1 text-sm text-neutral-500 hover:text-primary transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t('contact.step2Back')}
                  </button>
                  <span className="text-neutral-300">|</span>
                  <p className="text-xs font-bold tracking-[0.14em] uppercase text-neutral-400">
                    {t('contact.step2Prefix')} — {categoryLabel}
                  </p>
                </div>

                <div className="flex flex-col gap-4">
                  <CategoryFields category={category} form={form} errors={errors} onChange={handleChange} />

                  <div className="border-t border-neutral-100 pt-4 mt-2">
                    <p className="text-xs font-bold tracking-[0.14em] uppercase text-neutral-400 mb-4">
                      {t('contact.contactDetailsTitle')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label={t('contact.fieldName')} required error={errors.name}>
                        <input type="text" className={inputCls(errors.name)} placeholder="Jan de Vries"
                          value={form.name} onChange={e => handleChange('name', e.target.value)} />
                      </Field>
                      <Field label={t('contact.fieldEmail')} required error={errors.email}>
                        <input type="email" className={inputCls(errors.email)} placeholder="jan@bedrijf.nl"
                          value={form.email} onChange={e => handleChange('email', e.target.value)} />
                      </Field>
                      <Field label={t('contact.fieldPhone')} error={errors.phone}>
                        <input type="tel" className={inputCls(errors.phone)} placeholder="+31 6 12 34 56 78"
                          value={form.phone} onChange={e => handleChange('phone', e.target.value)} />
                      </Field>
                      <Field label={t('contact.fieldCompany')}
                        required={category === 'verkoop'} error={errors.company}>
                        <input type="text" className={inputCls(errors.company)} placeholder="Uw Bedrijf B.V."
                          value={form.company} onChange={e => handleChange('company', e.target.value)} />
                      </Field>
                    </div>
                  </div>

                  <Button type="submit" variant="primary" disabled={submitting} className="mt-2 self-start px-8">
                    {submitting ? t('contact.submitting') : t('contact.submitBtn')}
                  </Button>
                </div>
              </form>
            )}

            {/* Step 3: success */}
            {step === 'success' && (
              <div className="flex flex-col items-start gap-4 py-8">
                <div className="flex items-center justify-center h-14 w-14 rounded-full bg-green-50">
                  <CheckCircle className="h-7 w-7 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-neutral-900">{t('contact.successTitle')}</h2>
                <p className="text-sm text-neutral-600 max-w-md leading-relaxed">
                  {t('contact.successMsg')}
                </p>
                <button
                  onClick={() => { setStep('category'); setForm(INITIAL_FORM); setCategory(null) }}
                  className="mt-2 text-sm font-medium text-primary hover:underline"
                >
                  {t('contact.successReset')}
                </button>
              </div>
            )}
          </div>

          {/* ── Right: sidebar ── */}
          <div className="flex flex-col gap-5">
            <div>
              <p className="text-xs font-bold tracking-[0.14em] uppercase text-neutral-400 mb-4">
                {t('contact.sidebarTitle')}
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center h-9 w-9 rounded-md bg-gcg-tint shrink-0">
                    <Mail className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 mb-0.5">E-mail</p>
                    <a href="mailto:info@globalcontrol.group" className="text-sm text-primary hover:underline">
                      info@globalcontrol.group
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center h-9 w-9 rounded-md bg-gcg-tint shrink-0">
                    <Phone className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 mb-0.5">Telefoon</p>
                    <a href="tel:+31299705078" className="text-sm text-neutral-700 hover:text-primary transition-colors">
                      +31 299 705 078
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center h-9 w-9 rounded-md bg-gcg-tint shrink-0">
                    <MapPin className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 mb-0.5">Adres</p>
                    <p className="text-sm text-neutral-700">Julianaweg 200<br />Volendam</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-neutral-200 bg-neutral-50 p-4">
              <p className="text-xs font-semibold text-neutral-700 mb-1">{t('contact.responseTitle')}</p>
              <p className="text-xs text-neutral-600 leading-relaxed">{t('contact.responseBody')}</p>
            </div>

            <div className="border border-neutral-200 bg-neutral-50 p-4">
              <p className="text-xs font-semibold text-neutral-700 mb-1">{t('contact.hoursTitle')}</p>
              <p className="text-xs text-neutral-600 leading-relaxed">{t('contact.hoursBody')}</p>
            </div>
          </div>

        </div>
      </PageContainer>
    </div>
  )
}
